import React, { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';

import { Check, ChevronDown, ChevronUp, Save, Trash, X } from "lucide-react";

import { Modal } from "./Index";
import { CustomSelect1, ICustomSelect1BaseProps, ISelectOption } from "../shared/Selects";
import { areEntitlementsEqual, isEntitlementsInvalid, ModalEntitlementForm } from "../sub/ModalEntitlementForm";

import { closeModal, getModalData, isModalActive, openModal } from "../../store/modal.slice";

import { useAddUserToSubClient, useGetSubClient, useGetSubClientUsers, useGetUsersListForSubClientUserSelection, useRemoveUserFromSubClient, useSaveSubClientUserEntitlements } from "../../hooks/sub_clients-cax";
import { useGetEntitlementsForAdminForm } from "../../hooks/entitlements";

import { EEntitlementSource, EProduct } from "../../types/misc.enum";
import { IEntitlementsData } from "../../types/misc.interface";
import { ISubClientUserListItem } from "../../types/services.interfaces/sub_clients-cax";

const MODAL_NAME = "MANAGE_SUB_CLIENT_USERS_MODAL";

export const ManageSubClientUsersModal = () => {
    const dispatch = useDispatch();

    const isModalOpen = useSelector(isModalActive(MODAL_NAME));
    const modalData = useSelector(getModalData(MODAL_NAME));

    const [expandedUserIds, setExpandedUserIds] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [processingUserId, setProcessingUserId] = useState<string | null>(null);

    const { data: subClientDetails } = useGetSubClient(modalData?.subClientId ?? null);
    const { data: subClientUsers } = useGetSubClientUsers(subClientDetails?.id ?? null);
    const { data: usersListForSelection } = useGetUsersListForSubClientUserSelection(subClientDetails?.id ?? null);
    const { mutate: addUserToSubClient } = useAddUserToSubClient();
    const { mutate: removeUserFromSubClient } = useRemoveUserFromSubClient();

    const isBusy = isProcessing || !!processingUserId;

    const usersOptions = useMemo<ISubClientUserSelectOption[]>(() => {
        if (!usersListForSelection) return [];

        return usersListForSelection.map(user => ({
            value: user.user_id,
            label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || 'Unknown user',
            current_sub_client_id: user.current_sub_client_id,
            current_sub_client_name: user.current_sub_client_name,
        }));
    }, [usersListForSelection]);

    const onCloseModal = () => {
        dispatch(closeModal(MODAL_NAME));
    };

    const resetForm = () => {
        setExpandedUserIds([]);
        setIsProcessing(false);
        setProcessingUserId(null);
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const handleUserSelection = (option: ISubClientUserSelectOption) => {
        if (!!option.current_sub_client_id || !subClientDetails?.id || !option.value) return;

        addUserToSubClient({
            subClientId: subClientDetails.id,
            userId: option.value,
            setIsProcessing,
        });
    };

    const toggleUserExpanded = (userId: string) => {
        setExpandedUserIds(prev => prev.includes(userId)
            ? prev.filter(id => id !== userId)
            : [...prev, userId]);
    };

    const handleRemoveUser = (userId: string, userLabel: string) => {
        if (isBusy || !subClientDetails?.id) return;

        dispatch(openModal({
            name: 'CONFIRMATION_MODAL',
            data: {
                topupLvl: 1,
                title: 'Remove User?',
                description: `Are you sure you want to remove "${userLabel}" from this sub client? All their entitlements will be removed as well.`,
                onConfirm: () => {
                    removeUserFromSubClient({
                        subClientId: subClientDetails.id,
                        userId,
                        setIsProcessing,
                    });
                },
            },
        }));
    };

    return (
        <Modal
            name={MODAL_NAME}
            title="Manage Sub Client Users"
            baseClass="manage_sub_client_users"
            topupLvl={0}
            onClose={onCloseModal}
            shouldCloseOnEsc={false}
            shouldCloseOnOverlayClick={false}
        >
            <button className="modal__close_btn" onClick={onCloseModal}><X /></button>
            <p className="color--primary text-xl text-bold mb-2xl">
                Manage users for {subClientDetails?.name ?? 'sub client'}
            </p>
            <div className="modal__sub_client_users_form">
                <div className="modal__sub_client_users_form__block">
                    <div className="modal__sub_client_users_form__block__left">
                        <p className="text-sm text-medium color--txt-secondary">Add users</p>
                    </div>
                    <div className="modal__sub_client_users_form__block__right">
                        <SubClientUserSelect
                            fluid
                            placeholder="Search for a user"
                            disabled={isBusy || !subClientDetails?.id}
                            options={usersOptions}
                            onSelectToggle={option => handleUserSelection(option)}
                            sub_client_id={subClientDetails?.id ?? 0}
                            selected={subClientUsers?.map(user => ({
                                value: user.user_id,
                                label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || 'Unknown user',
                                current_sub_client_id: user.sub_client_user_id,
                                current_sub_client_name: subClientDetails?.name ?? null,
                            })) ?? []}
                            onRemoveUser={(option) => handleRemoveUser(option.value ?? '', option.label)}
                        />
                    </div>
                </div>

                <div className="modal__sub_client_users_form__users_section">
                    <p className="text-sm text-medium color--txt-secondary mb-lg">Users</p>
                    {!subClientUsers?.length && (
                        <div className="modal__sub_client_users_form__users_empty">
                            <p className="text-sm color--txt-muted">No users assigned to this sub client yet.</p>
                        </div>
                    )}
                    {!!subClientUsers?.length && (
                        <div className="modal__sub_client_users_form__users_list">
                            {subClientUsers.map(user => {
                                const isExpanded = expandedUserIds.includes(user.user_id);
                                const userLabel = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email || 'Unknown user';

                                return (
                                    <div key={user.user_id} className="modal__sub_client_users_form__user_card">
                                        <div className="modal__sub_client_users_form__user_card__header">
                                            <button
                                                type="button"
                                                className="modal__sub_client_users_form__user_card__header__toggle"
                                                onClick={() => toggleUserExpanded(user.user_id)}
                                                disabled={isBusy}
                                            >
                                                <div>
                                                    <p className="text-sm text-medium">{userLabel}</p>
                                                    {!!user.email && (
                                                        <p className="text-xs color--txt-secondary">{user.email}</p>
                                                    )}
                                                </div>
                                                {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn--icon-sm btn--radius-sm btn--secondary modal__sub_client_users_form__user_card__header__delete"
                                                onClick={() => handleRemoveUser(user.user_id, userLabel)}
                                                disabled={isBusy}
                                            >
                                                <Trash />
                                            </button>
                                        </div>
                                        {isExpanded && !!subClientDetails?.id && (
                                            <SubClientUserEntitlementSection
                                                user={user}
                                                subClientId={subClientDetails.id}
                                                isBusy={isBusy}
                                                isSaving={processingUserId === user.user_id}
                                                setProcessingUserId={setProcessingUserId}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

interface ISubClientUserEntitlementSectionProps {
    user: ISubClientUserListItem;
    subClientId: number;
    isBusy: boolean;
    isSaving: boolean;
    setProcessingUserId: Dispatch<SetStateAction<string | null>>;
};

const SubClientUserEntitlementSection = (props: ISubClientUserEntitlementSectionProps) => {
    const { user, subClientId, isBusy, isSaving, setProcessingUserId } = props;

    const [currentEntitlements, setCurrentEntitlements] = useState<IEntitlementsData>({
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    });

    const { data: availableEntitlements } = useGetEntitlementsForAdminForm(user.sub_client_user_id.toString(), EEntitlementSource.SUB_CLIENT_USER);
    const { mutate: saveSubClientUserEntitlements } = useSaveSubClientUserEntitlements();

    const isDataSame = areEntitlementsEqual(currentEntitlements, user.entitlements);

    const handleSave = () => {
        if (isBusy || isDataSame || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)) {
            return;
        };

        const products = currentEntitlements.products.map(product => product.key);
        const mbtsModules = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_modules.map(module => module.id) : [];
        const mcsaModules = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_modules.map(module => module.id) : [];
        const mbtsAssessments = products.includes(EProduct.MBTS) ? currentEntitlements.mbts_assessments : 0;
        const mcsaAssessments = products.includes(EProduct.MCSA) ? currentEntitlements.mcsa_assessments : 0;

        saveSubClientUserEntitlements({
            userId: user.user_id,
            subClientId,
            payload: {
                entitlements: {
                    products,
                    mbts_modules: mbtsModules,
                    mcsa_modules: mcsaModules,
                    mbts_assessments: mbtsAssessments,
                    mcsa_assessments: mcsaAssessments,
                },
            },
            setProcessingUserId,
        });
    };

    return (
        <div className="modal__sub_client_users_form__user_card__body">
            {!availableEntitlements?.maximum_caps?.products?.length && (
                <p className="text-sm color--txt-secondary mt-lg">
                    There are no available entitlements for this user. Please setup the entitlements for sub client first. If already set, all of them might have been allocated already, please increase the limit from client side.
                </p>
            )}
            {!!availableEntitlements?.maximum_caps?.products?.length && (
                <Fragment>
                    <ModalEntitlementForm
                        isModalOpen={false}
                        source={EEntitlementSource.SUB_CLIENT_USER}
                        isDivider={false}
                        isTitle={false}
                        availableEntitlements={availableEntitlements}
                        entitlements={user.entitlements}
                        onEntitlementsChange={setCurrentEntitlements}
                        isProcessing={isBusy}
                        assessmentsTooltip="Maximum number of {PRODUCT_NAME} assessments allowed for this user."
                    />
                    <div className="modal__sub_client_users_form__user_card__actions">
                        <button
                            type="button"
                            className="btn btn--sm btn--radius-sm btn--primary"
                            onClick={handleSave}
                            disabled={isBusy || isDataSame || isEntitlementsInvalid(currentEntitlements, availableEntitlements ?? null)}
                        >
                            <Save />
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                            {isSaving && <span className="btn-loader" />}
                        </button>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

interface ISubClientUserSelectOption extends ISelectOption {
    current_sub_client_id: number | null;
    current_sub_client_name: string | null;
};
interface ISubClientUserSelect extends ICustomSelect1BaseProps {
    options: ISubClientUserSelectOption[];
    onSelectToggle: (value: ISubClientUserSelectOption) => void;
    sub_client_id: number;
    selected: ISubClientUserSelectOption[];
    onRemoveUser: (option: ISubClientUserSelectOption) => void;
};
const SubClientUserSelect = (props: ISubClientUserSelect) => {
    const { placeholder, preInputIcon, postInputIcon, options, size = 'sm', fluid, disabled, onSelectToggle, sub_client_id, selected, onRemoveUser } = props;

    return (
        <CustomSelect1<ISubClientUserSelectOption>
            placeholder={placeholder}
            preInputIcon={preInputIcon}
            postInputIcon={postInputIcon}
            options={options}
            selected={selected}
            size={size}
            fluid={fluid}
            disabled={disabled}
            onSelectToggle={onSelectToggle}
            isOptionDisabled={(option) => !!option.current_sub_client_id && option.current_sub_client_id !== sub_client_id}
            onUnselect={onRemoveUser}
            renderOption={(option, forHiddenMeasure = false) => (
                <Fragment>
                    {option.label}
                    {(forHiddenMeasure || (!!option.current_sub_client_id && option.current_sub_client_id === sub_client_id)) && <Check />}
                    {(forHiddenMeasure || (!!option.current_sub_client_id && option.current_sub_client_id !== sub_client_id)) && (
                        <span className='text-xs color--txt-secondary'>{option.current_sub_client_name}</span>
                    )}
                </Fragment>
            )}
        />
    );
};
